import { ArrowLeft, ArrowRight, CircleHelp } from "lucide-react";
import { type ComponentType, useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { PAYOUT_BATCH_APPROVAL_SETTINGS_PATH } from "@/modules/dashboard/settings/constants/payoutBatchApprovalSection";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { usePayoutBatchApprovalPolicy } from "../../hooks/usePayoutBatchApprovalPolicy";
import {
  ApprovalScene,
  ReviewScene,
  RouteScene,
  RunScene,
  SelfApprovalScene,
  TemplateScene,
  UploadScene,
} from "./BatchGuideScenes";
import { batchGuideSteps, type GuideScene } from "./batchGuideSteps";

const SCENES: Record<GuideScene, ComponentType> = {
  route: RouteScene,
  template: TemplateScene,
  upload: UploadScene,
  review: ReviewScene,
  approval: ApprovalScene,
  self_approval: SelfApprovalScene,
  run: RunScene,
};

const LAST_STEP = batchGuideSteps(null).length - 1;

/**
 * "How it works": the whole life of a batch, one animated step at a time, always opening on the first. Who
 * approves follows the reader's organization, read when the guide opens; staff always get the standard
 * story.
 */
export function BatchGuideButton() {
  const { t } = useTranslations("payoutBatches");
  const { user, isInternalUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const { policy } = usePayoutBatchApprovalPolicy(
    open && !isInternalUser ? (user?.organization_id ?? null) : null,
  );
  const steps = batchGuideSteps(policy?.self_approval_allowed ? policy.reason : null);

  const goTo = useCallback((next: number) => setStep(Math.min(LAST_STEP, Math.max(0, next))), []);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowRight") setStep((current) => Math.min(LAST_STEP, current + 1));
      if (event.key === "ArrowLeft") setStep((current) => Math.max(0, current - 1));
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const current = steps[step];
  const Scene = SCENES[current.scene];

  return (
    <>
      <ColocaButton
        variant="ghost"
        icon={CircleHelp}
        onClick={() => {
          setStep(0);
          setOpen(true);
        }}
      >
        {t("guide_open")}
      </ColocaButton>

      <ColocaDialog
        open={open}
        onOpenChange={setOpen}
        size="lg"
        mobileFullscreen
        title={t("guide_title")}
        description={t("guide_description")}
        toolbar={
          <div className="flex items-center justify-between gap-3">
            <ol className="flex items-center gap-1">
              {steps.map((guideStep, index) => (
                <li key={guideStep.title}>
                  <button
                    type="button"
                    onClick={() => goTo(index)}
                    aria-label={t("guide_go_to_step", { step: index + 1, title: t(guideStep.title) })}
                    aria-current={index === step ? "step" : undefined}
                    className="flex h-6 items-center rounded-full px-0.5 outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <span
                      className={cn(
                        "block h-1.5 rounded-full transition-all duration-300 motion-reduce:transition-none",
                        index === step
                          ? "w-6 bg-primary"
                          : index < step
                            ? "w-1.5 bg-primary/50"
                            : "w-1.5 bg-border",
                      )}
                    />
                  </button>
                </li>
              ))}
            </ol>
            <span className="font-mono text-xs text-muted-foreground tabular-nums">
              {t("guide_progress", { current: step + 1, total: steps.length })}
            </span>
          </div>
        }
        footer={
          <div className="flex w-full items-center justify-between gap-2">
            <ColocaButton
              variant="ghost"
              icon={ArrowLeft}
              onClick={() => goTo(step - 1)}
              disabled={step === 0}
            >
              {t("guide_previous")}
            </ColocaButton>
            {step === LAST_STEP ? (
              <ColocaButton variant="primary" onClick={() => setOpen(false)}>
                {t("guide_done")}
              </ColocaButton>
            ) : (
              <ColocaButton variant="primary" iconTrailing={ArrowRight} onClick={() => goTo(step + 1)}>
                {t("guide_next")}
              </ColocaButton>
            )}
          </div>
        }
      >
        <div className="space-y-5 pb-1">
          <div
            aria-hidden="true"
            className="flex h-52 items-center justify-center overflow-hidden rounded-xl border border-border bg-muted/40 px-4"
          >
            <Scene key={step} />
          </div>
          <div aria-live="polite" className="min-h-32">
            <div
              key={step}
              className="space-y-1.5 animate-fade-up animate-duration-300 motion-reduce:animate-none"
            >
              <p className="font-mono text-[10px] tracking-wider text-primary uppercase">
                {t(current.phase)}
              </p>
              <h3 className="text-lg font-semibold text-foreground">{t(current.title)}</h3>
              <p className="text-sm leading-relaxed text-muted-foreground">{t(current.body)}</p>
              {current.aboutApproval && !isInternalUser && (
                <Link
                  to={PAYOUT_BATCH_APPROVAL_SETTINGS_PATH}
                  onClick={() => setOpen(false)}
                  className="inline-block pt-1 text-sm font-medium text-foreground underline underline-offset-4 hover:text-primary"
                >
                  {t("guide_approval_settings_link")}
                </Link>
              )}
            </div>
          </div>
        </div>
      </ColocaDialog>
    </>
  );
}
